import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatPhoneNumber } from '../utils/formatPhone'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHelpButton from '../components/PageHelpButton'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Avatar,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  Autocomplete,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Send as SendIcon,
  CheckCircle as DeliveredIcon,
  Schedule as QueuedIcon,
  Error as FailedIcon,
  Chat as ChatIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  LinkOff as LinkIcon,
  OpenInNew as OpenInNewIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material'
import { SMSMessage, messagesApi, Client, clientsApi, attachmentsApi, Lead, leadsApi } from '../services/api'
import { colors } from '../theme'
import { useNotification } from '../hooks/useNotification'
import { useWebSocket } from '../hooks/useWebSocket'
import { useClientSuccessMap } from '../hooks/useClientSuccessMap'
import { useIsMobile } from '../hooks/useIsMobile'
import ClientPicker from '../components/common/ClientPicker'
import { useAtom } from 'jotai'
import { userAtom } from '../state/auth'
import { isAdminOrManager } from '../utils/roles'

const SIDEBAR_WIDTH = 360

// Status icons for outbound messages (white/light colors for blue bubbles)
const statusIcons: Record<string, React.ReactNode> = {
  queued: <QueuedIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />,
  sent: <QueuedIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.9)' }} />,
  delivered: <DeliveredIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.9)' }} />,
  failed: <FailedIcon fontSize="small" sx={{ color: '#ffcdd2' }} />,
  received: null,
}

interface Conversation {
  clientId: string
  clientName: string
  phoneNumber: string
  lastMessage: SMSMessage
  unreadCount: number
}

export default function Messages() {
  const navigate = useNavigate()
  const [user] = useAtom(userAtom)
  const [searchParams, setSearchParams] = useSearchParams()
  const { showSuccess, showError } = useNotification()
  const isMobile = useIsMobile()
  const { load: loadCsData, getClientSuccessName } = useClientSuccessMap()
  const [allMessages, setAllMessages] = useState<SMSMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClientId, setSelectedClientId] = useState<string | null>(
    searchParams.get('clientId')
  )
  // MWA-335 — arriving from a client record used to seed the thread on the
  // right and nothing else, so the list beside it still showed every
  // conversation in the business. This is the filter, kept separate from
  // selectedClientId because clicking another thread reassigns that one.
  const filterClientId = searchParams.get('clientId')
  const [fetchedFilterName, setFetchedFilterName] = useState<string | null>(null)
  const [mobileShowChat, setMobileShowChat] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [chatImages, setChatImages] = useState<{ file: File; preview: string }[]>([])
  const [uploadingChatImage, setUploadingChatImage] = useState(false)
  const chatImageInputRef = useRef<HTMLInputElement>(null)

  // Compose state
  const [composeOpen, setComposeOpen] = useState(false)
  const [clients, setClients] = useState<Client[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [composeClient, setComposeClient] = useState<Client | null>(null)
  const [composeMessage, setComposeMessage] = useState('')
  const [composeSending, setComposeSending] = useState(false)
  const [manualPhoneMode, setManualPhoneMode] = useState(false)
  const [manualPhone, setManualPhone] = useState('')
  const [composeImages, setComposeImages] = useState<{ file: File; preview: string }[]>([])
  const [uploadingImage, setUploadingImage] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)

  // MMS image URL cache - maps S3 key to presigned URL
  const [mmsImageUrls, setMmsImageUrls] = useState<Record<string, string>>({})

  // Link to contact dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkContactType, setLinkContactType] = useState<'lead' | 'client'>('lead')
  const [leads, setLeads] = useState<Lead[]>([])
  const [loadingLeads, setLoadingLeads] = useState(false)
  const [selectedContact, setSelectedContact] = useState<Lead | Client | null>(null)
  const [updateContactPhone, setUpdateContactPhone] = useState(true)
  const [linking, setLinking] = useState(false)

  // WebSocket handler for real-time message updates
  const handleSmsEvent = useCallback(
    (message: { action: string; data: unknown }) => {
      const smsData = message.data as SMSMessage

      if (message.action === 'sms_sent' || message.action === 'sms_received') {
        // Add new message if not already in the list
        setAllMessages((prev) => {
          if (prev.some((m) => m.id === smsData.id)) return prev
          return [...prev, smsData]
        })
      } else if (message.action === 'sms_status_update') {
        // Update existing message status
        setAllMessages((prev) =>
          prev.map((m) =>
            m.id === smsData.id ? { ...m, status: smsData.status, deliveredAt: smsData.deliveredAt } : m
          )
        )
      }
    },
    []
  )

  // Set up WebSocket connection with handlers. `send` is used below to emit
  // `mark_client_read` when the user opens a client conversation.
  const { send: wsSend } = useWebSocket({
    sms_sent: handleSmsEvent,
    sms_received: handleSmsEvent,
    sms_status_update: handleSmsEvent,
  })

  // When the user opens a client's conversation, zero that client's
  // unreadSmsCount so the pipeline card badge clears. Fire-and-forget — if
  // the WS is mid-connect, the next thread switch retries.
  useEffect(() => {
    if (!selectedClientId) return
    // Skip the `phone:*` pseudo-IDs used for unmatched inbound numbers.
    if (selectedClientId.startsWith('phone:')) return
    wsSend('mark_client_read', { clientId: selectedClientId, channel: 'sms' })
  }, [selectedClientId, wsSend])

  useEffect(() => {
    loadMessages()
    loadCsData()
  }, [])

  useEffect(() => {
    // Scroll to bottom when messages change for selected conversation
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [allMessages, selectedClientId])

  // Load MMS image URLs when messages change
  useEffect(() => {
    const loadMmsUrls = async () => {
      // Find all S3 keys in mediaUrls that we haven't loaded yet
      const s3KeysToLoad: string[] = []

      for (const msg of allMessages) {
        if (msg.mediaUrls && msg.mediaUrls.length > 0) {
          for (const url of msg.mediaUrls) {
            // S3 keys don't start with "http" (e.g. "mms/..." or "client/...")
            if (!url.startsWith('http') && !mmsImageUrls[url]) {
              s3KeysToLoad.push(url)
            }
          }
        }
      }

      if (s3KeysToLoad.length === 0) return

      // Load URLs for each S3 key
      const newUrls: Record<string, string> = {}
      for (const s3Key of s3KeysToLoad) {
        try {
          const { downloadUrl } = await attachmentsApi.getMmsMediaUrl(s3Key)
          newUrls[s3Key] = downloadUrl
        } catch (err) {
          console.error(`Failed to load MMS URL for ${s3Key}:`, err)
        }
      }

      if (Object.keys(newUrls).length > 0) {
        setMmsImageUrls((prev) => ({ ...prev, ...newUrls }))
      }
    }

    loadMmsUrls()
  }, [allMessages, mmsImageUrls])

  const loadMessages = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await messagesApi.listAll()
      setAllMessages(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error loading messages:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to load messages'
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        setError('Unable to connect to server. Please check your connection and try again.')
      } else {
        setError(errorMessage)
      }
      setAllMessages([])
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    if ((!newMessage.trim() && chatImages.length === 0) || !selectedClientId) return

    const selectedConversation = conversations.find((c) => c.clientId === selectedClientId)
    if (!selectedConversation?.phoneNumber) {
      showError('Client does not have a phone number')
      return
    }

    // Upload images first if any
    let mediaUrls: string[] = []
    let mediaS3Keys: string[] = []
    if (chatImages.length > 0) {
      try {
        setUploadingChatImage(true)
        for (const img of chatImages) {
          const attachment = await attachmentsApi.upload(img.file, 'client', selectedClientId, 'MMS image', false)
          const { downloadUrl } = await attachmentsApi.generateDownloadUrl(attachment.id, 'preview')
          mediaUrls.push(downloadUrl)
          mediaS3Keys.push(attachment.s3Key)
        }
      } catch (err) {
        console.error('Error uploading images:', err)
        showError('Failed to upload images')
        setUploadingChatImage(false)
        return
      } finally {
        setUploadingChatImage(false)
      }
    }

    try {
      setSending(true)
      if (selectedClientId.startsWith('phone:')) {
        const phoneNumber = selectedConversation.phoneNumber
        await messagesApi.sendToPhone(phoneNumber, newMessage.trim(), mediaUrls.length > 0 ? mediaUrls : undefined, mediaS3Keys.length > 0 ? mediaS3Keys : undefined)
      } else {
        await messagesApi.send(selectedClientId, newMessage.trim(), undefined, mediaUrls.length > 0 ? mediaUrls : undefined, mediaS3Keys.length > 0 ? mediaS3Keys : undefined)
      }
      // Don't add message here - WebSocket will deliver it to avoid duplicates
      setNewMessage('')
      // Clear images
      chatImages.forEach((img) => URL.revokeObjectURL(img.preview))
      setChatImages([])
      showSuccess('Message sent')
    } catch (err) {
      console.error('Error sending message:', err)
      showError(err instanceof Error ? err.message : 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const handleChatImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const newImages: { file: File; preview: string }[] = []
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        showError(`${file.name} is not an image`)
        continue
      }
      const preview = URL.createObjectURL(file)
      newImages.push({ file, preview })
    }

    setChatImages((prev) => [...prev, ...newImages])

    if (chatImageInputRef.current) {
      chatImageInputRef.current.value = ''
    }
  }

  const handleRemoveChatImage = (index: number) => {
    setChatImages((prev) => {
      const removed = prev[index]
      URL.revokeObjectURL(removed.preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const loadClients = async () => {
    try {
      setLoadingClients(true)
      // `all: true` because the bare call is assignment-scoped, and anyone with
      // no assigned clients — every admin, among others — got an empty list and
      // a "no clients with phone numbers found" that was not true. Email
      // compose has always read the full list; this is the same picker, so the
      // two disagreeing about who exists was the actual bug.
      const data = await clientsApi.list({ all: true })
      // Filter to clients with phone numbers
      const clientsWithPhone = data.filter((c) => c.phone && c.phone.trim() !== '')
      setClients(clientsWithPhone)
    } catch (err) {
      console.error('Error loading clients:', err)
      showError('Failed to load clients')
    } finally {
      setLoadingClients(false)
    }
  }

  const isManager = isAdminOrManager(user) || user?.role === 'client_success'

  // clientIds the logged-in staff member is assigned to (coach / clientSuccess /
  // assignedTo). Drives the "My Clients" group on the compose picker.
  const assignedClientIds = useMemo(() => {
    const email = user?.email
    if (!email) return new Set<string>()
    return new Set(
      clients
        .filter((c) => c.coach === email || c.clientSuccess === email || c.assignedTo === email)
        .map((c) => c.id)
    )
  }, [clients, user?.email])

  // Only clients with a number, since that is what the text goes to — filtered
  // here rather than trusted from the load, because the link-to-contact dialog
  // refills the same list with everyone. Sorted mine-first because the picker's
  // grouping relies on same-group options being adjacent.
  const composeClientOptions = useMemo(() => {
    return [...clients]
      .filter((c) => c.phone && c.phone.trim() !== '')
      .sort((a, b) => {
        if (isManager) {
          const aMine = assignedClientIds.has(a.id) ? 0 : 1
          const bMine = assignedClientIds.has(b.id) ? 0 : 1
          if (aMine !== bMine) return aMine - bMine
        }
        return (a.name || a.email || '').localeCompare(b.name || b.email || '')
      })
  }, [clients, isManager, assignedClientIds])

  const handleOpenCompose = () => {
    setComposeOpen(true)
    loadClients()
  }

  const handleCloseCompose = () => {
    setComposeOpen(false)
    setComposeClient(null)
    setComposeMessage('')
    setManualPhoneMode(false)
    setManualPhone('')
    // Clear image previews
    composeImages.forEach((img) => URL.revokeObjectURL(img.preview))
    setComposeImages([])
  }

  // Link to contact dialog handlers
  const handleOpenLinkDialog = () => {
    setLinkDialogOpen(true)
    setSelectedContact(null)
    setLinkContactType('lead')
    setUpdateContactPhone(true)
    loadLeadsForLink()
    loadClientsForLink()
  }

  const handleCloseLinkDialog = () => {
    setLinkDialogOpen(false)
    setSelectedContact(null)
  }

  const loadLeadsForLink = async () => {
    try {
      setLoadingLeads(true)
      const data = await leadsApi.list()
      setLeads(data)
    } catch (err) {
      console.error('Error loading leads:', err)
    } finally {
      setLoadingLeads(false)
    }
  }

  const loadClientsForLink = async () => {
    try {
      setLoadingClients(true)
      // Same reason as loadClients: linking an unknown number to a contact was
      // impossible for anyone the scoped list returned nothing for.
      const data = await clientsApi.list({ all: true })
      setClients(data)
    } catch (err) {
      console.error('Error loading clients:', err)
    } finally {
      setLoadingClients(false)
    }
  }

  const handleLinkToContact = async () => {
    if (!selectedContact || !selectedClientId) return

    // Extract phone number from clientId (format: "phone:1234567890")
    const phoneNumber = selectedClientId.replace('phone:', '')

    try {
      setLinking(true)
      const result = await messagesApi.linkToContact(
        phoneNumber,
        selectedContact.id,
        linkContactType,
        updateContactPhone
      )

      showSuccess(`Linked ${result.messagesUpdated} messages to ${result.contactName}`)
      handleCloseLinkDialog()

      // Update selected conversation to the new contact ID
      setSelectedClientId(selectedContact.id)

      // Reload messages to reflect the changes
      loadMessages()
    } catch (err) {
      console.error('Error linking messages:', err)
      showError(err instanceof Error ? err.message : 'Failed to link messages')
    } finally {
      setLinking(false)
    }
  }

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const newImages: { file: File; preview: string }[] = []
    for (const file of Array.from(files)) {
      // Only accept images
      if (!file.type.startsWith('image/')) {
        showError(`${file.name} is not an image`)
        continue
      }
      // Create preview URL
      const preview = URL.createObjectURL(file)
      newImages.push({ file, preview })
    }

    setComposeImages((prev) => [...prev, ...newImages])

    // Clear input
    if (imageInputRef.current) {
      imageInputRef.current.value = ''
    }
  }

  const handleRemoveImage = (index: number) => {
    setComposeImages((prev) => {
      const removed = prev[index]
      URL.revokeObjectURL(removed.preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleComposeSend = async () => {
    // Allow sending message with just images (no text)
    if (!composeMessage.trim() && composeImages.length === 0) return

    // Upload images first if any
    let mediaUrls: string[] = []
    let mediaS3Keys: string[] = []
    if (composeImages.length > 0) {
      try {
        setUploadingImage(true)
        for (const img of composeImages) {
          // Upload to S3 and get a presigned download URL
          const entityId = composeClient?.id || 'mms-upload'
          const attachment = await attachmentsApi.upload(img.file, 'client', entityId, 'MMS image', false)
          const { downloadUrl } = await attachmentsApi.generateDownloadUrl(attachment.id, 'preview')
          mediaUrls.push(downloadUrl)
          mediaS3Keys.push(attachment.s3Key)
        }
      } catch (err) {
        console.error('Error uploading images:', err)
        showError('Failed to upload images')
        setUploadingImage(false)
        return
      } finally {
        setUploadingImage(false)
      }
    }

    if (manualPhoneMode) {
      // Sending to manual phone number
      if (!manualPhone.trim()) {
        showError('Please enter a phone number')
        return
      }

      // Strip formatting and add +1 country code
      const digits = manualPhone.replace(/\D/g, '')
      if (digits.length !== 10) {
        showError('Please enter a valid 10-digit phone number')
        return
      }
      const formattedPhone = `+1${digits}`

      try {
        setComposeSending(true)
        await messagesApi.sendToPhone(formattedPhone, composeMessage.trim(), mediaUrls.length > 0 ? mediaUrls : undefined, mediaS3Keys.length > 0 ? mediaS3Keys : undefined)
        showSuccess('Message sent')
        handleCloseCompose()
        loadMessages()
      } catch (err) {
        console.error('Error sending message:', err)
        showError(err instanceof Error ? err.message : 'Failed to send message')
      } finally {
        setComposeSending(false)
      }
    } else {
      // Sending to existing client
      if (!composeClient) {
        showError('Please select a client')
        return
      }

      if (!composeClient.phone) {
        showError('Selected client does not have a phone number')
        return
      }

      try {
        setComposeSending(true)
        await messagesApi.send(composeClient.id, composeMessage.trim(), undefined, mediaUrls.length > 0 ? mediaUrls : undefined, mediaS3Keys.length > 0 ? mediaS3Keys : undefined)
        showSuccess('Message sent')
        handleCloseCompose()
        loadMessages()
      } catch (err) {
        console.error('Error sending message:', err)
        showError(err instanceof Error ? err.message : 'Failed to send message')
      } finally {
        setComposeSending(false)
      }
    }
  }

  const handleComposeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleComposeSend()
    }
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Now'
    if (diffMins < 60) return `${diffMins}m`
    if (diffHours < 24) return `${diffHours}h`
    if (diffDays < 7) return `${diffDays}d`
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Filter messages by the client in the URL, then by the search term
  const filteredMessages = allMessages.filter((msg) => {
    if (filterClientId && msg.clientId !== filterClientId) return false
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      msg.body.toLowerCase().includes(term) ||
      msg.clientName?.toLowerCase().includes(term) ||
      msg.phoneNumber.includes(term)
    )
  })

  // The messages name the client themselves whenever there are any. When there
  // are none — the case that used to discard the clientId in silence — the
  // record is read so the chip and the empty state can still say who.
  const nameFromMessages = useMemo(
    () =>
      filterClientId
        ? allMessages.find((m) => m.clientId === filterClientId)?.clientName
        : undefined,
    [filterClientId, allMessages]
  )
  const filterClientName = nameFromMessages || fetchedFilterName

  useEffect(() => {
    if (!filterClientId || nameFromMessages) return
    let cancelled = false
    clientsApi
      .get(filterClientId)
      .then((c) => {
        if (!cancelled) setFetchedFilterName(c.name || c.email || null)
      })
      .catch(() => {
        // Non-fatal: the chip falls back to naming the filter without the
        // client, which still beats an inbox that looks unfiltered.
      })
    return () => {
      cancelled = true
    }
  }, [filterClientId, nameFromMessages])

  // Build conversations from messages
  const conversationMap = new Map<string, Conversation>()
  filteredMessages.forEach((msg) => {
    const existing = conversationMap.get(msg.clientId)
    if (!existing) {
      conversationMap.set(msg.clientId, {
        clientId: msg.clientId,
        clientName: msg.clientName || 'Unknown',
        phoneNumber: msg.phoneNumber,
        lastMessage: msg,
        unreadCount: 0,
      })
    } else if (new Date(msg.createdAt) > new Date(existing.lastMessage.createdAt)) {
      existing.lastMessage = msg
    }
  })

  const conversations = Array.from(conversationMap.values()).sort(
    (a, b) => new Date(b.lastMessage.createdAt).getTime() - new Date(a.lastMessage.createdAt).getTime()
  )

  // Get messages for selected conversation
  const selectedMessages = selectedClientId
    ? allMessages
        .filter((msg) => msg.clientId === selectedClientId)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    : []

  const selectedConversation = conversations.find((c) => c.clientId === selectedClientId)

  // Group messages by date
  const groupedMessages: { date: string; messages: SMSMessage[] }[] = []
  let currentDate = ''
  selectedMessages.forEach((msg) => {
    const msgDate = formatDate(msg.createdAt)
    if (msgDate !== currentDate) {
      currentDate = msgDate
      groupedMessages.push({ date: msgDate, messages: [msg] })
    } else {
      groupedMessages[groupedMessages.length - 1].messages.push(msg)
    }
  })

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: 56, sm: 64 },
        left: { xs: 0, sm: 260 },
        right: 0,
        bottom: 0,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      {/* Sidebar - Conversation List */}
      <Paper
        sx={{
          width: { xs: '100%', sm: SIDEBAR_WIDTH },
          flexShrink: 0,
          display: isMobile && mobileShowChat ? 'none' : 'flex',
          flexDirection: 'column',
          borderRight: { xs: 0, sm: 1 },
          borderColor: 'divider',
          borderRadius: 0,
          overflow: 'hidden',
        }}
        elevation={0}
      >
        {/* Sidebar Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Messages
              </Typography>
              <PageHelpButton articleId="messages-send" />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton onClick={handleOpenCompose} size="small" title="New Message" color="primary">
                <AddIcon />
              </IconButton>
              <IconButton onClick={loadMessages} size="small" title="Refresh">
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          <TextField
            fullWidth
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'grey.100',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ m: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* The filter has to be visible and reversible, or a narrowed inbox
            reads as an empty one. Same chip-with-onDelete pattern the
            needs-assignment filter uses on Clients.tsx. View Client sits here
            rather than only inside an opened thread, which was the dead end QA
            reported: arriving from a client record and having no way back. */}
        {filterClientId && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`Showing: ${filterClientName || 'one client'}`}
              onDelete={() => setSearchParams({})}
            />
            {/* Same guard as the thread header below: an unlinked thread is
                keyed `phone:<number>`, so this would be a dead route. */}
            {!filterClientId.startsWith('phone:') && (
              <Button
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={() => navigate(`/clients/${filterClientId}`)}
                sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
              >
                View Client
              </Button>
            )}
          </Box>
        )}

        {/* Conversation List */}
        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {conversations.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              {filterClientId && !searchTerm ? (
                <>
                  <ChatIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    No SMS with {filterClientName || 'this client'} yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Messages you send them will appear here.
                  </Typography>
                  <Button variant="outlined" size="small" onClick={handleOpenCompose}>
                    New Message
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? 'No conversations match your search' : 'No conversations yet'}
                </Typography>
              )}
            </Box>
          ) : (
            conversations.map((conv, index) => (
              <Box key={conv.clientId}>
                <ListItemButton
                  selected={selectedClientId === conv.clientId}
                  onClick={() => {
                    setSelectedClientId(conv.clientId)
                    if (isMobile) setMobileShowChat(true)
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: colors.primary, width: 48, height: 48 }}>
                      {getInitials(conv.clientName)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: conv.lastMessage.direction === 'inbound' ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: { xs: 'calc(100vw - 140px)', sm: 180 },
                          }}
                        >
                          {conv.clientName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                          {getClientSuccessName(conv.clientId) && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'success.main',
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              CS: {getClientSuccessName(conv.clientId)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(conv.lastMessage.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: conv.lastMessage.direction === 'inbound' ? 500 : 400,
                        }}
                      >
                        {conv.lastMessage.direction === 'outbound' && 'You: '}
                        {conv.lastMessage.body}
                      </Typography>
                    }
                  />
                </ListItemButton>
                {index < conversations.length - 1 && <Divider sx={{ ml: 9 }} />}
              </Box>
            ))
          )}
        </List>
      </Paper>

      {/* Main Chat Panel */}
      <Box sx={{ flex: 1, display: isMobile && !mobileShowChat ? 'none' : 'flex', flexDirection: 'column', backgroundColor: 'grey.50', overflow: 'hidden' }}>
        {selectedClientId && selectedConversation ? (
          <>
            {/* Chat Header */}
            <Paper
              sx={{
                p: 2,
                borderRadius: 0,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
              }}
              elevation={0}
            >
              {isMobile && (
                <IconButton onClick={() => setMobileShowChat(false)} size="small">
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Avatar sx={{ bgcolor: colors.primary, width: 40, height: 40 }}>
                {getInitials(selectedConversation.clientName)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedConversation.clientName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatPhoneNumber(selectedConversation.phoneNumber)}
                </Typography>
              </Box>
              {/* Show Link to Contact button for unknown numbers, or View Client for known clients */}
              {selectedClientId.startsWith('phone:') ? (
                isMobile ? (
                  <IconButton size="small" onClick={handleOpenLinkDialog} title="Link to Contact">
                    <LinkIcon />
                  </IconButton>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LinkIcon />}
                    onClick={handleOpenLinkDialog}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Link to Contact
                  </Button>
                )
              ) : (
                isMobile ? (
                  <IconButton size="small" onClick={() => navigate(`/clients/${selectedClientId}`)} title="View Client">
                    <OpenInNewIcon />
                  </IconButton>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => navigate(`/clients/${selectedClientId}`)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    View Client
                  </Button>
                )
              )}
            </Paper>

            {/* Messages Area */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {selectedMessages.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No messages yet. Start a conversation!
                  </Typography>
                </Box>
              ) : (
                <>
                  {groupedMessages.map((group) => (
                    <Box key={group.date}>
                      {/* Date separator */}
                      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <Chip
                          label={group.date}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0,0,0,0.08)',
                            fontWeight: 500,
                          }}
                        />
                      </Box>

                      {/* Messages for this date */}
                      {group.messages.map((msg) => (
                        <Box
                          key={msg.id}
                          sx={{
                            display: 'flex',
                            justifyContent: msg.direction === 'outbound' ? 'flex-end' : 'flex-start',
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: { xs: '85%', sm: '65%' },
                              p: 1.5,
                              px: 2,
                              borderRadius: msg.direction === 'outbound' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                              backgroundColor: msg.direction === 'outbound' ? colors.primary : 'white',
                              color: msg.direction === 'outbound' ? 'white' : 'text.primary',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            }}
                          >
                            {/* Author name for outbound messages */}
                            {msg.direction === 'outbound' && msg.authorName && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  opacity: 0.8,
                                  mb: 0.5,
                                  fontWeight: 500,
                                }}
                              >
                                {msg.authorName.includes('@')
                                  ? msg.authorName
                                      .split('@')[0]
                                      .split('.')
                                      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                                      .join(' ')
                                  : msg.authorName}
                              </Typography>
                            )}

                            {/* Media images (MMS) */}
                            {msg.mediaUrls && msg.mediaUrls.length > 0 && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: msg.body ? 1 : 0 }}>
                                {msg.mediaUrls.map((url, idx) => {
                                  // Determine the actual URL to use
                                  // S3 keys don't start with "http" (e.g. "mms/..." or "client/...")
                                  const isS3Key = !url.startsWith('http')
                                  const imageUrl = isS3Key ? mmsImageUrls[url] : url

                                  if (!imageUrl) {
                                    // Still loading the URL
                                    return (
                                      <Box
                                        key={idx}
                                        sx={{
                                          width: 200,
                                          height: 150,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: 1,
                                          backgroundColor: msg.direction === 'outbound' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
                                        }}
                                      >
                                        <CircularProgress size={24} sx={{ color: msg.direction === 'outbound' ? 'white' : 'inherit' }} />
                                      </Box>
                                    )
                                  }

                                  return (
                                    <Box
                                      key={idx}
                                      component="a"
                                      href={imageUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        display: 'block',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        maxWidth: 280,
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={imageUrl}
                                        alt={`MMS image ${idx + 1}`}
                                        sx={{
                                          display: 'block',
                                          maxWidth: '100%',
                                          maxHeight: 300,
                                          borderRadius: 1,
                                        }}
                                        onError={(e) => {
                                          // Fallback to link if image fails to load
                                          const target = e.target as HTMLImageElement
                                          target.style.display = 'none'
                                          target.parentElement!.innerHTML = `
                                            <div style="display: flex; align-items: center; gap: 8px; padding: 8px;">
                                              <span>Failed to load image</span>
                                            </div>
                                          `
                                        }}
                                      />
                                    </Box>
                                  )
                                })}
                              </Box>
                            )}

                            {/* Message body */}
                            {msg.body && (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                                {msg.body}
                              </Typography>
                            )}

                            {/* Time and status */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  opacity: 0.7,
                                  fontSize: '0.7rem',
                                }}
                              >
                                {formatTime(msg.createdAt)}
                              </Typography>
                              {msg.direction === 'outbound' && statusIcons[msg.status]}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </Box>

            {/* Input Area */}
            <Paper
              sx={{
                p: 2,
                borderRadius: 0,
                borderTop: 1,
                borderColor: 'divider',
              }}
              elevation={0}
            >
              {/* Image previews */}
              {chatImages.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {chatImages.map((img, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        width: 60,
                        height: 60,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <img
                        src={img.preview}
                        alt={`Attachment ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveChatImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          p: 0.25,
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {uploadingChatImage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Uploading...
                  </Typography>
                </Box>
              )}

              {/* Hidden file input */}
              <input
                type="file"
                ref={chatImageInputRef}
                onChange={handleChatImageSelect}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
              />

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <IconButton
                  onClick={() => chatImageInputRef.current?.click()}
                  disabled={sending || uploadingChatImage}
                  size="small"
                  title="Attach image"
                  sx={{ color: 'text.secondary' }}
                >
                  <ImageIcon />
                </IconButton>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending || uploadingChatImage}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      backgroundColor: 'grey.100',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={sending || uploadingChatImage || (!newMessage.trim() && chatImages.length === 0)}
                  sx={{
                    minWidth: 48,
                    height: 48,
                    borderRadius: '50%',
                    p: 0,
                  }}
                >
                  {(sending || uploadingChatImage) ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                </Button>
              </Box>
            </Paper>
          </>
        ) : (
          /* Empty State - No conversation selected */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'text.secondary',
            }}
          >
            <ChatIcon sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Select a conversation
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Choose a conversation from the list to start messaging
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCompose}
            >
              New Message
            </Button>
          </Box>
        )}
      </Box>

      {/* Compose Drawer */}
      <Drawer
        anchor="right"
        open={composeOpen}
        onClose={handleCloseCompose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.paper',
          },
        }}
      >
        {/* Compose Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'background.paper',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            New Message
          </Typography>
          <IconButton onClick={handleCloseCompose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Compose Content */}
        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 2, backgroundColor: 'background.paper' }}>
          {/* Toggle between client select and manual entry */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={manualPhoneMode ? 'outlined' : 'contained'}
              size="small"
              onClick={() => {
                setManualPhoneMode(false)
                setManualPhone('')
              }}
              sx={{ flex: 1 }}
            >
              Select Client
            </Button>
            <Button
              variant={manualPhoneMode ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setManualPhoneMode(true)
                setComposeClient(null)
              }}
              sx={{ flex: 1 }}
            >
              Enter Phone
            </Button>
          </Box>

          {manualPhoneMode ? (
            <TextField
              label="Phone Number"
              placeholder="(555) 123-4567"
              value={manualPhone}
              onChange={(e) => {
                // Format phone number as user types
                const digits = e.target.value.replace(/\D/g, '')
                let formatted = ''
                if (digits.length > 0) {
                  if (digits.length <= 3) {
                    formatted = `(${digits}`
                  } else if (digits.length <= 6) {
                    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`
                  } else {
                    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
                  }
                }
                setManualPhone(formatted)
              }}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                  '& input': { color: 'text.primary' },
                },
              }}
            />
          ) : (
            <ClientPicker
              options={composeClientOptions}
              value={composeClient}
              onChange={setComposeClient}
              loading={loadingClients}
              isManager={isManager}
              assignedClientIds={assignedClientIds}
              secondary={(c) => formatPhoneNumber(c.phone)}
              label="Select Client"
              placeholder="Search for a client..."
              noOptionsText={loadingClients ? 'Loading...' : 'No clients with phone numbers found'}
              sx={{
                width: '100%',
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                  '& input': { color: 'text.primary' },
                },
              }}
            />
          )}

          <TextField
            label="Message"
            multiline
            rows={4}
            value={composeMessage}
            onChange={(e) => setComposeMessage(e.target.value)}
            onKeyDown={handleComposeKeyDown}
            placeholder="Type your message..."
            disabled={composeSending || uploadingImage}
            fullWidth
            sx={{
              '& .MuiInputLabel-root': { color: 'text.secondary' },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.default',
                '& textarea': { color: 'text.primary' },
              },
            }}
          />

          {/* Image previews */}
          {composeImages.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {composeImages.map((img, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <img
                    src={img.preview}
                    alt={`Attachment ${index + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(index)}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      p: 0.25,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {uploadingImage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Uploading images...
              </Typography>
            </Box>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            ref={imageInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            multiple
            style={{ display: 'none' }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
            <IconButton
              onClick={() => imageInputRef.current?.click()}
              disabled={composeSending || uploadingImage}
              title="Attach image"
              sx={{ color: 'text.secondary' }}
            >
              <ImageIcon />
            </IconButton>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleCloseCompose} disabled={composeSending || uploadingImage} sx={{ color: 'text.primary' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleComposeSend}
                disabled={composeSending || uploadingImage || (manualPhoneMode ? !manualPhone.trim() : !composeClient) || (!composeMessage.trim() && composeImages.length === 0)}
                startIcon={(composeSending || uploadingImage) ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              >
                {uploadingImage ? 'Uploading...' : composeSending ? 'Sending...' : 'Send'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Link to Contact Dialog */}
      <Dialog open={linkDialogOpen} onClose={handleCloseLinkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Link Messages to Contact</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Link all messages from this phone number to an existing lead or client. Only contacts without phone numbers are shown.
          </Typography>

          {/* Contact type toggle */}
          <ToggleButtonGroup
            value={linkContactType}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setLinkContactType(value)
                setSelectedContact(null)
              }
            }}
            sx={{ mb: 2 }}
            fullWidth
          >
            <ToggleButton value="lead">Lead</ToggleButton>
            <ToggleButton value="client">Client</ToggleButton>
          </ToggleButtonGroup>

          {/* Contact selector */}
          {linkContactType === 'lead' ? (
            <Autocomplete
              options={leads.filter(lead => !lead.phone)}
              loading={loadingLeads}
              value={selectedContact as Lead | null}
              onChange={(_, newValue) => setSelectedContact(newValue)}
              getOptionLabel={(option) => `${option.name}${option.email ? ` (${option.email})` : ''}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Lead"
                  placeholder="Search leads..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingLeads ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email || 'No email'}
                    </Typography>
                  </Box>
                </li>
              )}
              sx={{ mb: 2 }}
              noOptionsText="No leads without phone numbers found"
            />
          ) : (
            <Autocomplete
              options={clients.filter(client => !client.phone)}
              loading={loadingClients}
              value={selectedContact as Client | null}
              onChange={(_, newValue) => setSelectedContact(newValue)}
              getOptionLabel={(option) => `${option.name}${option.email ? ` (${option.email})` : ''}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Client"
                  placeholder="Search clients..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingClients ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email || 'No email'}
                    </Typography>
                  </Box>
                </li>
              )}
              sx={{ mb: 2 }}
              noOptionsText="No clients without phone numbers found"
            />
          )}

          {/* Option to update contact's phone number */}
          <FormControlLabel
            control={
              <Checkbox
                checked={updateContactPhone}
                onChange={(e) => setUpdateContactPhone(e.target.checked)}
              />
            }
            label={`Update ${linkContactType}'s phone number to this number`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog} disabled={linking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLinkToContact}
            disabled={linking || !selectedContact}
            startIcon={linking ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
          >
            {linking ? 'Linking...' : 'Link Messages'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
